import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button
} from '@mui/material';
import { Pie, Doughnut, Line } from 'react-chartjs-2';
import html2pdf from 'html2pdf.js';
import api from "../api/api";
import 'chart.js/auto';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import Swal from 'sweetalert2';
import SchoolLogo from './messiahlogo.png'; // optional logo import if you have one

const StudentStatsDashboard = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = localStorage.getItem('access');
        const res = await api.get('/student-stats/', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchStats();
  }, []);

  if (!stats) return <Typography>Loading...</Typography>;

  // --- Chart Data ---
  const pieData = {
    labels: Object.keys(stats.status_summary),
    datasets: [
      {
        data: Object.values(stats.status_summary),
        backgroundColor: ['#1976d2', '#ff9800', '#4caf50']
      }
    ]
  };

  const doughnutData = {
    labels: Object.keys(stats.gender_summary),
    datasets: [
      {
        data: Object.values(stats.gender_summary),
        backgroundColor: ['#e91e63', '#3f51b5']
      }
    ]
  };

  const areaData = {
    labels: Object.keys(stats.grade_gender_summary),
    datasets: [
      {
        label: 'Male',
        data: Object.values(stats.grade_gender_summary).map(g => g.male),
        backgroundColor: 'rgba(33, 150, 243, 0.3)',
        borderColor: '#2196f3',
        fill: true
      },
      {
        label: 'Female',
        data: Object.values(stats.grade_gender_summary).map(g => g.female),
        backgroundColor: 'rgba(233, 30, 99, 0.3)',
        borderColor: '#e91e63',
        fill: true
      }
    ]
  };

  // --- PDF Download ---
const handleDownloadPDF = () => {
  const content = `
    <div style="margin-bottom:15px;">
      <div style="display:flex; align-items:center; justify-content:center; gap:10px; text-align:center;">
        ${
          SchoolLogo
            ? `<img src="${SchoolLogo}" alt="School Logo" style="width:60px; height:60px;"/>`
            : ""
        }
        <div>
          <h2 style="margin:4px 0; font-size:18px;">Messiah Mission Institute</h2>
          <p style="margin:2px 0; font-size:14px;">Students Statistics Overview</p>
        </div>
      </div>
      <h3 style="margin-top:10px; text-align:center; font-size:15px;">Summary Data Overview</h3>
    </div>

    <!-- Status Summary Table -->
    <table style="width:100%; border-collapse:collapse; font-size:12px; margin-bottom:15px;">
      <thead>
        <tr style="background:#d3d3d3; color:#000; text-align:left;">
          <th style="border:1px solid #999; padding:5px;">Category</th>
          <th style="border:1px solid #999; padding:5px;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${Object.entries(stats.status_summary)
          .map(
            ([key, value], i) => `
          <tr style="background:${i % 2 === 0 ? '#f9f9f9' : '#efefef'}; color:#000;">
            <td style="border:1px solid #ccc; padding:5px;">${key}</td>
            <td style="border:1px solid #ccc; padding:5px;">${value}</td>
          </tr>`
          )
          .join("")}
      </tbody>
    </table>

    <!-- Gender Summary Table -->
    <table style="width:100%; border-collapse:collapse; font-size:12px; margin-bottom:15px;">
      <thead>
        <tr style="background:#d3d3d3; color:#000; text-align:left;">
          <th style="border:1px solid #999; padding:5px;">Gender</th>
          <th style="border:1px solid #999; padding:5px;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${Object.entries(stats.gender_summary)
          .map(
            ([key, value], i) => `
          <tr style="background:${i % 2 === 0 ? '#f9f9f9' : '#efefef'}; color:#000;">
            <td style="border:1px solid #ccc; padding:5px;">${key}</td>
            <td style="border:1px solid #ccc; padding:5px;">${value}</td>
          </tr>`
          )
          .join("")}
      </tbody>
    </table>

    <!-- Male/Female per Grade Class -->
    <table style="width:100%; border-collapse:collapse; font-size:12px;">
      <thead>
        <tr style="background:#d3d3d3; color:#000; text-align:left;">
          <th style="border:1px solid #999; padding:5px;">Grade Class</th>
          <th style="border:1px solid #999; padding:5px;">Male</th>
          <th style="border:1px solid #999; padding:5px;">Female</th>
        </tr>
      </thead>
      <tbody>
        ${Object.entries(stats.grade_gender_summary)
          .map(
            ([grade, val], i) => `
          <tr style="background:${i % 2 === 0 ? '#f9f9f9' : '#efefef'}; color:#000;">
            <td style="border:1px solid #ccc; padding:5px;">${grade}</td>
            <td style="border:1px solid #ccc; padding:5px;">${val.male}</td>
            <td style="border:1px solid #ccc; padding:5px;">${val.female}</td>
          </tr>`
          )
          .join("")}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="3" style="text-align:center; padding:5px; background:#e0e0e0; color:#000; font-size:11px;">
            Polu Integrated School Management System
          </td>
        </tr>
      </tfoot>
    </table>
  `;

  const opt = {
    margin: 0.3,
    filename: "Students_Statistics.pdf",
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
    pagebreak: { mode: ["avoid-all", "css", "legacy"] },
  };

  html2pdf().from(content).set(opt).save();
};


  return (
    <Box sx={{ p: 3 }}>
      <Button
        variant="contained"
        color="primary"
        onClick={handleDownloadPDF}
        sx={{ mb: 3 }}
      >
        Download PDF
      </Button>

      {/* Dashboard Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {Object.entries(stats.status_summary).map(([key, value]) => (
          <Grid item xs={12} sm={4} key={key}>
            <Card sx={{ minHeight: 120, backgroundColor: '#e3f2fd' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <SchoolIcon sx={{ fontSize: 40, mr: 2, color: '#1976d2' }} />
                <Box>
                  <Typography variant="h6">{key}</Typography>
                  <Typography variant="h4">{value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {Object.entries(stats.gender_summary).map(([key, value]) => (
          <Grid item xs={12} sm={6} key={key}>
            <Card sx={{ minHeight: 120, backgroundColor: '#fce4ec' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, mr: 2, color: '#e91e63' }} />
                <Box>
                  <Typography variant="h6">{key}</Typography>
                  <Typography variant="h4">{value}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Student Status
              </Typography>
              <Pie data={pieData} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: '#e8eaf6' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Gender Distribution
              </Typography>
              <Doughnut data={doughnutData} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Male/Female per Grade Class
              </Typography>
              <Line data={areaData} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default StudentStatsDashboard;
